import { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { formatAuditTimestamp } from "@/lib/auditFormat";
import { formatIsoDateShort } from "@/lib/bookingDates";
import { aclRuleForPath, isAllowed } from "@/lib/acl";
import { useCurrentUser } from "@/lib/auth";
import { isAppAdmin } from "@/lib/permissions";
import {
  TICKET_CATEGORIES,
  TICKET_CATEGORY_LABELS,
  TICKET_PRIORITIES,
  TICKET_PRIORITY_LABELS,
  categoryLabel,
  formatTicketNumber,
} from "@/lib/supportTickets";
import { usePageHeader } from "@/layout/usePageHeader";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { ClickableTableRow } from "@/components/ui/ClickableTableRow";
import { Table } from "@/components/ui/Table";
import { CreateTicketModal } from "@/pages/tickets/CreateTicketModal";
import { TicketPriorityBadge, TicketStatusBadge } from "@/pages/tickets/ticketUi";
import type { TicketCounts, TicketListResponse, TicketStatus } from "@/types/supportTicket";

const PAGE_SIZE = 25;

type StatusFilter = "" | "actionable" | TicketStatus;
type CountKey = Exclude<keyof TicketCounts, "can_process">;

const STATUS_CHIPS: { id: StatusFilter; label: string; countKey?: CountKey }[] = [
  { id: "actionable", label: "Zu erledigen", countKey: "actionable" },
  { id: "open", label: "Offen", countKey: "open" },
  { id: "in_progress", label: "In Bearbeitung", countKey: "in_progress" },
  { id: "waiting", label: "Wartend", countKey: "waiting" },
  { id: "resolved", label: "Erledigt", countKey: "resolved" },
  { id: "closed", label: "Geschlossen", countKey: "closed" },
  { id: "", label: "Alle" },
];

export function TicketsPage() {
  const navigate = useNavigate();
  const { setPageMeta } = usePageHeader();
  const { user, loading: userLoading } = useCurrentUser();
  const groups = user?.groups ?? [];
  const admin = isAppAdmin(groups);
  const allowed = isAllowed(groups, aclRuleForPath("/tickets"));

  const [status, setStatus] = useState<StatusFilter | null>(null);
  const [priority, setPriority] = useState("");
  const [category, setCategory] = useState("");
  const [assignment, setAssignment] = useState("");
  const [queryInput, setQueryInput] = useState("");
  const [query, setQuery] = useState("");
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState<TicketListResponse["items"]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<TicketCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    setPageMeta({
      title: "Tickets",
      subtitle: admin
        ? "Alle Tickets. Die Gruppe Camping-Admin kann zuweisen, kommentieren und abschließen."
        : "Ihre Meldungen. Die Gruppe Camping-Admin bearbeitet die Tickets.",
    });
    return () => setPageMeta(null);
  }, [admin, setPageMeta]);

  useEffect(() => {
    if (userLoading || !user) return;
    setStatus((current) => current ?? (isAppAdmin(user.groups ?? []) ? "actionable" : ""));
  }, [user, userLoading]);

  const load = useCallback(() => {
    if (status === null) return;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      limit: String(PAGE_SIZE),
      offset: String(offset),
    });
    if (status) params.set("status", status);
    if (priority) params.set("priority", priority);
    if (category) params.set("category", category);
    if (admin && assignment) params.set("assignment", assignment);
    if (query.trim()) params.set("q", query.trim());

    Promise.all([
      api.get<TicketListResponse>(`/api/v1/tickets?${params}`),
      api.get<TicketCounts>("/api/v1/tickets/summary"),
    ])
      .then(([list, summary]) => {
        setItems(list.items);
        setTotal(list.total);
        setCounts(summary);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Tickets konnten nicht geladen werden.");
      })
      .finally(() => setLoading(false));
  }, [admin, assignment, category, offset, priority, query, status]);

  useEffect(() => {
    if (!allowed || status === null) return;
    load();
  }, [allowed, load, status]);

  function selectStatus(next: StatusFilter) {
    setOffset(0);
    setStatus(next);
  }

  if (userLoading || !user) {
    return <p className="text-sm text-label-secondary">Sitzung wird geprüft …</p>;
  }

  if (!allowed) {
    return (
      <Alert variant="error" title="Keine Berechtigung">
        Tickets sind für Ihre Rolle nicht freigegeben.
      </Alert>
    );
  }

  const allCount = counts
    ? counts.open + counts.in_progress + counts.waiting + counts.resolved + counts.closed
    : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {STATUS_CHIPS.map((chip) => {
            const active = status === chip.id;
            const count = chip.id === "" ? allCount : chip.countKey && counts ? counts[chip.countKey] : null;
            return (
              <button
                key={chip.id || "all"}
                type="button"
                aria-pressed={active}
                onClick={() => selectStatus(chip.id)}
                className={clsx(
                  "rounded-full px-3 py-1.5 text-sm font-medium transition-colors",
                  active
                    ? "bg-accent text-label-on-accent"
                    : "bg-fill text-label hover:bg-fill-secondary",
                )}
              >
                {chip.label}
                {count !== null ? <span className="ml-1 tabular-nums opacity-80">{count}</span> : null}
              </button>
            );
          })}
        </div>
        <Button type="button" onClick={() => setCreateOpen(true)}>
          Neues Ticket
        </Button>
      </div>

      <form
        className="grid gap-3 rounded-xl border border-separator bg-surface-secondary/30 p-4 sm:grid-cols-2 lg:grid-cols-4"
        onSubmit={(e) => {
          e.preventDefault();
          const nextQuery = queryInput;
          const queryChanged = nextQuery !== query;
          if (offset !== 0) setOffset(0);
          if (queryChanged) {
            setQuery(nextQuery);
            return;
          }
          if (offset === 0) load();
        }}
      >
        <label className="block text-sm sm:col-span-2">
          <span className="mb-1 block text-label-secondary">Suche</span>
          <input
            className="eh-input w-full"
            value={queryInput}
            onChange={(e) => setQueryInput(e.target.value)}
            placeholder="Titel, Beschreibung, Person oder Nummer"
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-label-secondary">Kategorie</span>
          <select
            className="eh-input w-full"
            value={category}
            onChange={(e) => {
              setOffset(0);
              setCategory(e.target.value);
            }}
          >
            <option value="">Alle</option>
            {TICKET_CATEGORIES.map((value) => (
              <option key={value} value={value}>
                {TICKET_CATEGORY_LABELS[value]}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-label-secondary">Priorität</span>
          <select
            className="eh-input w-full"
            value={priority}
            onChange={(e) => {
              setOffset(0);
              setPriority(e.target.value);
            }}
          >
            <option value="">Alle</option>
            {TICKET_PRIORITIES.map((value) => (
              <option key={value} value={value}>
                {TICKET_PRIORITY_LABELS[value]}
              </option>
            ))}
          </select>
        </label>
        {admin ? (
          <label className="block text-sm">
            <span className="mb-1 block text-label-secondary">Zuweisung</span>
            <select
              className="eh-input w-full"
              value={assignment}
              onChange={(e) => {
                setOffset(0);
                setAssignment(e.target.value);
              }}
            >
              <option value="">Alle</option>
              <option value="me">Mir zugewiesen</option>
              <option value="unassigned">Nicht zugewiesen</option>
            </select>
          </label>
        ) : null}
        <div className="flex items-end">
          <Button type="submit" variant="secondary" disabled={loading}>
            Filtern
          </Button>
        </div>
      </form>

      {error ? <p className="text-sm text-status-red">{error}</p> : null}

      {loading || status === null ? (
        <p className="text-sm text-label-secondary">Tickets werden geladen …</p>
      ) : (
        <>
          <p className="text-sm text-label-secondary">
            {total} Ticket{total === 1 ? "" : "s"}
            {total > PAGE_SIZE
              ? ` (Seite ${Math.floor(offset / PAGE_SIZE) + 1} von ${Math.ceil(total / PAGE_SIZE)})`
              : ""}
          </p>
          <Table
            wide
            emptyMessage={
              admin
                ? "Keine Tickets für diesen Filter."
                : "Sie haben noch kein Ticket angelegt."
            }
            isEmpty={items.length === 0}
          >
            <thead>
              <tr>
                <th>Nr.</th>
                <th>Titel</th>
                <th>Status</th>
                <th>Priorität</th>
                <th>Kategorie</th>
                <th>Gemeldet von</th>
                {admin ? <th>Zugewiesen</th> : null}
                <th>Aktualisiert</th>
              </tr>
            </thead>
            <tbody>
              {items.map((ticket) => (
                <ClickableTableRow
                  key={ticket.id}
                  ariaLabel={`${formatTicketNumber(ticket.number)} ${ticket.title}`}
                  onActivate={() => navigate(`/tickets/${ticket.id}`)}
                >
                  <td className="tabular-nums font-medium" data-label="Nr.">
                    {formatTicketNumber(ticket.number)}
                  </td>
                  <td data-label="Titel">
                    <span className="font-medium">{ticket.title}</span>
                    {ticket.preview ? (
                      <span className="mt-0.5 block text-xs text-label-secondary">{ticket.preview}</span>
                    ) : null}
                    {ticket.due_on ? (
                      <span className="mt-0.5 block text-xs text-label-tertiary">
                        Fällig {formatIsoDateShort(ticket.due_on)}
                      </span>
                    ) : null}
                  </td>
                  <td data-label="Status">
                    <TicketStatusBadge status={ticket.status} />
                  </td>
                  <td data-label="Priorität">
                    <TicketPriorityBadge priority={ticket.priority} />
                  </td>
                  <td className="text-label-secondary" data-label="Kategorie">
                    {categoryLabel(ticket.category)}
                  </td>
                  <td data-label="Gemeldet von">{ticket.requester_name}</td>
                  {admin ? (
                    <td className="text-label-secondary" data-label="Zugewiesen">
                      {ticket.assignee_name || "—"}
                    </td>
                  ) : null}
                  <td className="text-label-secondary" data-label="Aktualisiert">
                    {formatAuditTimestamp(ticket.updated_at)}
                  </td>
                </ClickableTableRow>
              ))}
            </tbody>
          </Table>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="secondary"
              disabled={offset === 0 || loading}
              onClick={() => setOffset((value) => Math.max(0, value - PAGE_SIZE))}
            >
              Zurück
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={offset + PAGE_SIZE >= total || loading}
              onClick={() => setOffset((value) => value + PAGE_SIZE)}
            >
              Weiter
            </Button>
          </div>
        </>
      )}

      <CreateTicketModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(ticket) => {
          setCreateOpen(false);
          navigate(`/tickets/${ticket.id}`);
        }}
      />
    </div>
  );
}
